/**
 * Notebook-specific Jupyter provider built on top of KernelPoolProvider.
 *
 * Uses useKernelConsumer('notebook') from the pool and layers on the
 * notebook-specific concerns: JWT forwarding, health debouncing,
 * watchdog teardown, Sentry enrichment, and the legacy useJupyter()
 * context shape that existing consumers (NotebookEditor, Cell,
 * StreamOnlyWindow) depend on.
 */

import * as Sentry from '@sentry/react';
import * as React from 'react';
import { useKernelConsumer } from '@/lib/hooks/useKernelConsumer';
import { classifyWatchdogAction } from '@/lib/jupyter/classifyWatchdogAction';
import type { JupyterClient, KernelHealth } from '@/lib/jupyter/JupyterClient';
import { KernelNotFoundError } from '@/lib/jupyter/JupyterClient';
import { type KernelInfo, KernelNotAvailableError, type KernelSpecInfo } from '@/lib/jupyter/KernelPool';
import type { WidgetManager } from '@/lib/jupyter/WidgetManager';
import { setRunningFilePath } from '@/lib/persistence';
import { updateKernelContext, updateReconnectionContext } from '@/lib/services/sentryEnrichment';
import type { WorkspaceState } from '@/lib/types/rome';
import { requestWorkspaceStatusRefresh } from '@/lib/workspaceStatus/events';
import { useKernelPool } from './KernelPoolProvider';
import { useWorkspace } from './WorkspaceProvider';

export interface JupyterContextValue {
  client: JupyterClient | null;
  getWidgetManager: () => Promise<WidgetManager | null>;
  isConnected: boolean;
  isConnecting: boolean;
  error: string | null;
  jupyterUrl: string | null;
  kernelHealth: KernelHealth;
  connect: () => Promise<void>;
  disconnect: () => Promise<void>;
  availableKernels: KernelInfo[];
  /** Kernelspecs registered on the Jupyter server, for the "new kernel" picker. */
  availableKernelSpecs: KernelSpecInfo[];
  /** Name of the server's default kernelspec, or null until fetched. */
  defaultKernelSpec: string | null;
  attachToKernel: (kernelId: string) => Promise<void>;
  startNewKernel: (displayName?: string, kernelspec?: string) => Promise<void>;
  detachKernel: () => void;
  /**
   * Terminate a single kernel by id, leaving the rest of the pool running. When
   * the killed kernel is the one this consumer is attached to, the consumer is
   * detached and the health state reset so the selector falls back cleanly.
   */
  killKernel: (kernelId: string) => Promise<void>;
}

export const JupyterContext = React.createContext<JupyterContextValue | null>(null);

const WATCHDOG_TIMEOUT_MS = 30_000;

const DEFAULT_HEALTH: KernelHealth = {
  status: 'unknown',
  connectionStatus: 'disconnected',
  isHealthy: false,
};

export function JupyterProvider({ children }: { children: React.ReactNode }) {
  const { connectedWorkspace, workspaces } = useWorkspace();
  const { pool, isConnected: isPoolConnected, connect: connectPool } = useKernelPool();
  const consumer = useKernelConsumer('notebook');

  const [isConnecting, setIsConnecting] = React.useState(false);
  const [error, setError] = React.useState<string | null>(null);
  const [kernelHealth, setKernelHealth] = React.useState<KernelHealth>(DEFAULT_HEALTH);

  const connectingRef = React.useRef(false);
  const reconnectCountRef = React.useRef(0);
  const firstDisconnectedAtRef = React.useRef<number | null>(null);

  // Derive the client from the consumer handle
  const client = consumer.handle?.client ?? null;
  const isConnected = consumer.handle?.isConnected ?? false;
  const jupyterUrl = pool?.baseUrl ?? null;

  // Track the connected workspace's current state in a ref so the watchdog
  // effect can read fresh state inside setTimeout without re-subscribing on
  // every workspace poll (which would reset the 30s timer). Updated on every
  // render -- the ref is the single source of truth for the timer callback.
  const wsStateRef = React.useRef<WorkspaceState | undefined>(undefined);
  wsStateRef.current = connectedWorkspace ? workspaces.find((w) => w.id === connectedWorkspace.id)?.state : undefined;

  // Health debouncing: immediately show busy/error, debounce busy->idle
  React.useEffect(() => {
    if (!consumer.handle) {
      setKernelHealth(DEFAULT_HEALTH);
      updateKernelContext(DEFAULT_HEALTH);
      return;
    }

    let debounceTimer: ReturnType<typeof setTimeout> | null = null;
    let lastHealth: KernelHealth | null = null;

    const unsubscribe = consumer.handle.onHealthChange((health) => {
      if (debounceTimer) {
        clearTimeout(debounceTimer);
        debounceTimer = null;
      }

      updateKernelContext(health);
      if (
        lastHealth &&
        (lastHealth.status !== health.status || lastHealth.connectionStatus !== health.connectionStatus)
      ) {
        requestWorkspaceStatusRefresh(`kernel:${lastHealth.status}->${health.status}`);
      }

      if (health.isHealthy && reconnectCountRef.current > 0) {
        reconnectCountRef.current = 0;
        firstDisconnectedAtRef.current = null;
        updateReconnectionContext(0, null);
      }

      const isTransitioningToIdle = lastHealth?.status === 'busy' && health.status === 'idle';

      if (isTransitioningToIdle) {
        debounceTimer = setTimeout(() => {
          setKernelHealth(health);
          debounceTimer = null;
        }, 300);
      } else {
        setKernelHealth(health);
      }

      lastHealth = health;
    });

    return () => {
      unsubscribe();
      if (debounceTimer) clearTimeout(debounceTimer);
    };
  }, [consumer.handle]);

  // Keep the KernelPool in sync when the underlying kernel id changes.
  //
  // ``client.restartKernel()`` and ``client.shutdownAllKernels()`` swap the
  // JupyterClient's ``_kernel`` under our nose without notifying the pool.
  // The pool's ``_kernels`` map is then stale: it still references the old
  // (shutdown) id, or is empty, while ``client.kernel.id`` points at the new
  // server kernel. The kernel selector reads its options from the pool, so
  // an unsynced pool leaves the selector stuck on a fallback label even
  // though the kernel is healthy and idle. Re-poll the server whenever the
  // active kernel id is one the pool doesn't know about.
  React.useEffect(() => {
    if (!consumer.handle || !pool) return;

    const sync = () => {
      const kernelId = consumer.handle?.client.kernel?.id ?? null;
      if (!kernelId) return;
      const known = pool.listKernels().some((k) => k.id === kernelId);
      if (!known) {
        pool.refresh().catch(() => {
          // refresh() already swallows transport errors; ignore here too.
        });
      }
    };

    sync();
    const unsubscribe = consumer.handle.onHealthChange(sync);
    return () => unsubscribe();
  }, [consumer.handle, pool]);

  // Watchdog: tear down stale handle after sustained disconnection.
  // Intentionally does NOT depend on `workspaces` -- it re-identities on
  // every workspace poll (~10s) and would reset the 30s timer before it
  // can fire. Workspace state is read from wsStateRef inside the timeout
  // so teardown transitions during the window are respected.
  React.useEffect(() => {
    if (!consumer.handle || !isConnected) return;
    if (kernelHealth.connectionStatus !== 'disconnected') return;

    // Kernel disconnection during workspace teardown is expected
    if (classifyWatchdogAction(0, wsStateRef.current) === 'ignore') return;

    const timer = setTimeout(() => {
      // Re-check teardown state at fire time -- the workspace may have
      // transitioned during the 30s window
      const currentWsState = wsStateRef.current;
      if (classifyWatchdogAction(0, currentWsState) === 'ignore') return;

      console.warn('[jupyter] watchdog: kernel disconnected for 30s, tearing down');

      reconnectCountRef.current += 1;
      if (firstDisconnectedAtRef.current === null) {
        firstDisconnectedAtRef.current = Date.now() - WATCHDOG_TIMEOUT_MS;
      }
      const totalDisconnectedMs = Date.now() - firstDisconnectedAtRef.current;
      updateReconnectionContext(reconnectCountRef.current, totalDisconnectedMs);

      const action = classifyWatchdogAction(reconnectCountRef.current, currentWsState);

      Sentry.addBreadcrumb({
        category: 'jupyter',
        message: `watchdog teardown (attempt ${reconnectCountRef.current})`,
        level: 'warning',
        data: {
          disconnectedForMs: WATCHDOG_TIMEOUT_MS,
          reconnectCount: reconnectCountRef.current,
          totalDisconnectedMs,
          workspaceId: connectedWorkspace?.id ?? null,
        },
      });

      if (action === 'error' && connectedWorkspace) {
        Sentry.captureException(new Error('Kernel reconnection loop: unable to restore connection'), {
          level: 'error',
          extra: {
            reconnectCount: reconnectCountRef.current,
            totalDisconnectedMs,
            workspaceId: connectedWorkspace.id,
            workspaceName: connectedWorkspace.name,
          },
        });
      }

      consumer.detach();
      setError(null);
    }, WATCHDOG_TIMEOUT_MS);

    return () => clearTimeout(timer);
  }, [consumer, isConnected, kernelHealth.connectionStatus, connectedWorkspace]);

  // Connect: ensure pool is connected, attach to existing kernel or start new
  const connect = React.useCallback(async () => {
    if (isConnecting || isConnected || connectingRef.current) return;

    connectingRef.current = true;
    setIsConnecting(true);
    setError(null);

    try {
      if (!isPoolConnected) {
        await connectPool();
      }

      // connectPool() already calls pool.refresh() internally. If the pool
      // was already connected we still refresh to pick up kernels created by
      // other consumers since last poll. refresh() dedupes concurrent calls
      // so this will not duplicate the in-flight request from connectPool.
      if (pool) {
        if (isPoolConnected) {
          await pool.refresh();
        }
        if (pool.listKernels().length > 0) {
          // Attach to a *live* kernel, not just the oldest-known one. After a
          // remote restart the pool can still list a dead kernel; attaching is
          // what validates liveness (a 404 surfaces as KernelNotFoundError).
          //
          // Walk kernels in pool order rather than hoisting "connected" ones
          // first: connectionStatus === 'connected' only means *some* consumer
          // is attached (e.g. the agent on a scratch kernel), not that it is
          // the notebook's sim kernel — reordering by it can attach the
          // notebook to the wrong warm scene. On a stale hit, refresh once so a
          // live replacement (started by another tab/consumer or a Jupyter
          // restart between the initial list and now) becomes visible, then
          // re-read the list and keep walking — including newly-discovered
          // kernels — instead of dead-ending on the pre-refresh snapshot and
          // starting a duplicate kernel that loses the existing warm state.
          const tried = new Set<string>();
          let refreshedAfterMiss = false;
          for (;;) {
            const candidate = pool.listKernels().find((k) => !tried.has(k.id));
            if (!candidate) break;
            tried.add(candidate.id);
            try {
              await consumer.attachTo(candidate.id);
              return;
            } catch (err) {
              if (err instanceof KernelNotFoundError) {
                if (!refreshedAfterMiss) {
                  await pool.refresh().catch((refreshErr: unknown) => {
                    console.debug('[jupyter] kernel-list refresh after stale attach failed', refreshErr);
                  });
                  refreshedAfterMiss = true;
                }
                continue;
              }
              throw err;
            }
          }
          // Every reachable kernel was stale -- fall through to startNew below.
        }
      }

      const notebookCount = pool
        ? pool.listKernels().filter((k) => k.displayName.startsWith('Notebook Kernel')).length
        : 0;
      await consumer.startNew(undefined, `Notebook Kernel ${notebookCount + 1}`);
    } catch (err) {
      const message = err instanceof Error ? err.message : 'Failed to connect';
      setError(message);
      Sentry.addBreadcrumb({
        category: 'jupyter',
        message: 'Connection failed',
        level: 'info',
        data: { error: message },
      });
    } finally {
      connectingRef.current = false;
      setIsConnecting(false);
    }
  }, [isConnecting, isConnected, isPoolConnected, connectPool, consumer, pool]);

  // Disconnect: detach from the kernel and clean up
  const disconnect = React.useCallback(async () => {
    if (consumer.handle) {
      try {
        const kernelId = consumer.handle.kernelId;
        consumer.detach();
        if (pool) {
          await pool.shutdownKernel(kernelId);
        }
      } catch (err) {
        console.warn('[jupyter] disconnect failed', {
          workspaceId: connectedWorkspace?.id ?? null,
          error: err instanceof Error ? err.message : String(err),
        });
      }
    }
    setKernelHealth(DEFAULT_HEALTH);
    updateKernelContext(DEFAULT_HEALTH);
    setError(null);
    setRunningFilePath(connectedWorkspace?.id ?? null, null);
  }, [consumer, pool, connectedWorkspace?.id]);

  // Reset reconnection tracking when workspace changes
  const prevWorkspaceIdRef = React.useRef<string | null>(null);
  React.useEffect(() => {
    const currentId = connectedWorkspace?.id ?? null;
    if (prevWorkspaceIdRef.current !== null && prevWorkspaceIdRef.current !== currentId) {
      reconnectCountRef.current = 0;
      firstDisconnectedAtRef.current = null;
      updateReconnectionContext(0, null);
      setRunningFilePath(prevWorkspaceIdRef.current, null);
      // A connect failure against the old workspace (e.g. during teardown)
      // must not survive as a red "Kernel error" chip on the next one.
      setError(null);
    }
    prevWorkspaceIdRef.current = currentId;
  }, [connectedWorkspace?.id]);

  const getWidgetManager = React.useCallback(async (): Promise<WidgetManager | null> => {
    if (!client) return null;
    return client.getWidgetManager();
  }, [client]);

  // Kernel switching: delegate to the consumer hook so the provider
  // state (client, health, etc.) updates automatically
  const attachToKernel = React.useCallback(
    async (kernelId: string) => {
      await consumer.attachTo(kernelId);
    },
    [consumer]
  );

  const startNewKernel = React.useCallback(
    async (displayName?: string, kernelspec?: string) => {
      const notebookCount = pool
        ? pool.listKernels().filter((k) => k.displayName.startsWith('Notebook Kernel')).length
        : 0;
      const resolvedDisplayName = displayName ?? `Notebook Kernel ${notebookCount + 1}`;
      // Narrative trail for whichever kernelspec the user picked in the
      // selector. ``kernelspec`` is undefined when only one spec is registered
      // (the picker is hidden and the pool falls back to antioch-sim).
      Sentry.addBreadcrumb({
        category: 'jupyter',
        message: 'starting new kernel',
        level: 'info',
        data: { kernelspec: kernelspec ?? 'default', displayName: resolvedDisplayName },
      });
      try {
        await consumer.startNew(kernelspec, resolvedDisplayName);
      } catch (err) {
        // Expected preconditions/transients -- breadcrumb only. The pool surfaces
        // an expired workspace token as a retry-able message error rather than a
        // bug, and a not-yet-connected pool is a precondition failure.
        const message = err instanceof Error ? err.message : String(err);
        const isExpected = err instanceof KernelNotAvailableError || message.includes('Jupyter authentication expired');
        if (isExpected) {
          Sentry.addBreadcrumb({
            category: 'jupyter',
            message: 'new kernel start failed (expected)',
            level: 'warning',
            data: { kernelspec: kernelspec ?? 'default', reason: message },
          });
        } else {
          // A real failure to start the chosen kernelspec (e.g. the spec is
          // unknown to the server or the kernel dies on launch). Group by
          // feature so every kernel-start bug lands in one issue regardless of
          // the underlying transport stack.
          Sentry.withScope((scope) => {
            scope.setFingerprint(['kernel-start-failure']);
            scope.setContext('kernel_start', {
              kernelspec: kernelspec ?? 'default',
              displayName: resolvedDisplayName,
              workspaceId: connectedWorkspace?.id ?? null,
            });
            Sentry.captureException(err);
          });
        }
        throw err;
      }
    },
    [consumer, pool, connectedWorkspace?.id]
  );

  const detachKernel = React.useCallback(() => {
    consumer.detach();
    setKernelHealth(DEFAULT_HEALTH);
    updateKernelContext(DEFAULT_HEALTH);
  }, [consumer]);

  const killKernel = React.useCallback(
    async (kernelId: string) => {
      if (!pool) return;
      const isCurrent = consumer.handle?.kernelId === kernelId;
      Sentry.addBreadcrumb({
        category: 'jupyter',
        message: 'killing kernel',
        level: 'info',
        data: { kernelId, isCurrent, workspaceId: connectedWorkspace?.id ?? null },
      });
      // Detach our consumer first so the derived client/health reset cleanly
      // before the kernel disappears from the server.
      if (isCurrent) {
        consumer.detach();
        setKernelHealth(DEFAULT_HEALTH);
        updateKernelContext(DEFAULT_HEALTH);
      }
      await pool.shutdownKernel(kernelId);
    },
    [pool, consumer, connectedWorkspace?.id]
  );

  const value = React.useMemo(
    () => ({
      client,
      getWidgetManager,
      isConnected,
      isConnecting,
      error,
      jupyterUrl,
      kernelHealth,
      connect,
      disconnect,
      availableKernels: consumer.availableKernels,
      availableKernelSpecs: consumer.availableKernelSpecs,
      defaultKernelSpec: consumer.defaultKernelSpec,
      attachToKernel,
      startNewKernel,
      detachKernel,
      killKernel,
    }),
    [
      client,
      getWidgetManager,
      isConnected,
      isConnecting,
      error,
      jupyterUrl,
      kernelHealth,
      connect,
      disconnect,
      consumer.availableKernels,
      consumer.availableKernelSpecs,
      consumer.defaultKernelSpec,
      attachToKernel,
      startNewKernel,
      detachKernel,
      killKernel,
    ]
  );

  return <JupyterContext.Provider value={value}>{children}</JupyterContext.Provider>;
}

export function useJupyter(): JupyterContextValue {
  const context = React.useContext(JupyterContext);
  if (!context) {
    throw new Error('useJupyter must be used within a JupyterProvider');
  }
  return context;
}
